import { createContext } from 'react';
import { FilterType, FilterValue } from '@/models';
import { ActiveFilter, FilterState } from '@/types/filter';

/**
 * Cascading filters: per-filter set of values that still return data for the
 * current combination of other active filters. Dynamic filters register their
 * available set as their option query resolves; consumers use it to drop
 * disabled (zero-result) selected values from the Cube query.
 */
export type AvailableValuesByFilter = Partial<Record<FilterType, Set<string>>>;

/**
 * Option paths each hierarchical filter's selection was made against, reduced to
 * what its builder reads. Persisted with the filter state so a refresh rebuilds
 * exactly the same Cube filter instead of falling back to different semantics.
 */
export type OptionPathsByFilter = Partial<Record<FilterType, string[]>>;

export type FiltersContextType = {
  filterState: FilterState;
  defaultFiltersState: FilterState;
  filterOverlayVisible: boolean;
  setFilterOverlayVisible: (visible: boolean) => void;
  handleApplyFilter: (filters: Partial<FilterState>) => void;
  replaceFilters: (filters: FilterState) => void;
  handleClearAllFilters: () => void;
  handleDeleteActiveFilter: (filterName: FilterType, value: FilterValue) => void;
  activeFilters: ActiveFilter[];
  numberActiveFilterCategories: number;
  filterConfig: readonly FilterType[];
  availableValuesByFilter?: AvailableValuesByFilter;
  registerAvailableValues?: (filterKey: FilterType, values: Set<string>) => void;
  optionPathsByFilter?: OptionPathsByFilter;
  /**
   * Records the paths a filter's *draft* selection was made against. Staged
   * values stay out of the registry until {@link commitOptionPaths}, so an
   * abandoned drawer draft can never drift from the applied filter state.
   */
  stageOptionPaths?: (filterKey: FilterType, paths: string[]) => void;
  /** Promotes the staged paths into the registry; call alongside handleApplyFilter. */
  commitOptionPaths?: () => void;
  /** Discards the staged paths, e.g. when the drawer is cancelled or closed. */
  clearStagedOptionPaths?: () => void;
  /** Replaces the whole registry, e.g. when a saved filter or shared view is applied. */
  replaceOptionPaths?: (paths: OptionPathsByFilter) => void;
};

const defaultContext = {
  filterState: {},
  defaultFiltersState: {},
  filterOverlayVisible: false,
  setFilterOverlayVisible: () => {},
  handleApplyFilter: () => {},
  replaceFilters: () => {},
  handleClearAllFilters: () => {},
  handleDeleteActiveFilter: () => {},
  activeFilters: [] as ActiveFilter[],
  numberActiveFilterCategories: 0,
  filterConfig: [] as FilterType[],
  availableValuesByFilter: {},
  registerAvailableValues: () => {},
  optionPathsByFilter: {},
  stageOptionPaths: () => {},
  commitOptionPaths: () => {},
  clearStagedOptionPaths: () => {},
  replaceOptionPaths: () => {},
};

const FiltersContext = createContext<FiltersContextType>(defaultContext);
export default FiltersContext;
